import LaunchIcon from "@mui/icons-material/Launch";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { runFreedayBidAward } from "../../api/bidAward";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system.js";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { clearFreedayAwardData, processFreedayBidAwardPayload } from "../../util/bid/bidAward";
import { getS3ObjectName, isAwardedBid, longPollingBidFetch } from "../../util/bid/bidCommon.js";
import { BID_AWARDED_FILTER_OPTIONS, BID_STATUS, FREEDAY_BID_AWARD_TYPE } from "../../util/bid/bidConstants";
import { buildFreedayBidAllotmentsPayload, buildTimeOffBidS3Data } from "../../util/bid/bidEventBuilder";
import { getEligibleEmployees } from "../../util/bid/bidGroupUtils";
import {
  getFreedayColumns,
  getReliefEmployeesCount,
  getStandardEmployeesCount,
  getTotalReliefAllotments,
  getTotalStandardAllotments,
  getUnAwardedTableColumns,
  hasUnawardedFreedayEmployees,
} from "../../util/bid/freedayBidUtils";
import { validateReliefAllotments, validateStandardAllotments } from "../../util/bid/freedayValidation";
import { copyJsonObject, sanitizeSpecialChars } from "../../util/commonUtils";
import { BID_AWARD_NO_BID_PAGE_TYPE, SELECT_FREEDAY_BID_TYPE_LIST } from "../../util/constants";
import { bidDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import { FREEDAY_BID_PUBLISH_DIALOG_MESSAGE } from "../../util/messageConstants";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ErrorMessage from "../ErrorMessage";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import StarsCloseIconButton from "../fundamentals/StarsCloseIconButton";
import SuccessDialog from "../fundamentals/SuccessDialog";
import FreedayBidAwardExport from "./FreedayBidAwardExport";

const FreedayBidAward = (props) => {
  const [isAddEmployeeConfirmationChecked, setIsAddEmployeeConfirmationChecked] = useState(false);
  const [isRemoveEmployeeConfirmationChecked, setIsRemoveEmployeeConfirmationChecked] = useState(false);
  const [isEligibilityEmployeeConfirmationChecked, setIsEligibilityEmployeeConfirmationChecked] = useState(false);
  const [isAwardRunning, setIsAwardRunning] = useState(false);
  const userSession = useSelector(selectUserSession);
  const [selectedAwardFilterRadioButton, setSelectedAwardFilterRadioButton] = useState(BID_AWARDED_FILTER_OPTIONS.ALL);
  const [selectedAwardFilterCheckBoxList, setSelectedAwardFilterCheckBoxList] = useState(
    Object.values(FREEDAY_BID_AWARD_TYPE)
  );
  const [selectedUnawardedFilterCheckBoxList, setSelectedUnawardedFilterCheckBoxList] = useState(
    Object.values(FREEDAY_BID_AWARD_TYPE).filter((type) => type !== FREEDAY_BID_AWARD_TYPE.AUTO_AWARDED)
  );
  const [selectedStandardReliefFilterCheckBoxList, setSelectedStandardReliefFilterCheckBoxList] =
    useState(SELECT_FREEDAY_BID_TYPE_LIST);
  const [filteredFreedayAwardRows, setFilteredFreedayAwardRows] = useState([]);
  const [filteredFreedayUnawardedRows, setFilteredFreedayUnawardedRows] = useState([]);
  const [isEnlargeBidAllotmentsDialogOpen, setIsEnlargeBidAllotmentsDialogOpen] = useState(false);
  const [isPublishConfirmationDialogOpen, setIsPublishConfirmationDialogOpen] = useState(false);
  const [showPublishLoader, setShowPublishLoader] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [publishErrors, setPublishErrors] = useState("");
  const [totalStandardEmployees, setTotalStandardEmployees] = useState(0);
  const [totalReliefEmployees, setTotalReliefEmployees] = useState(0);

  const [standardAllotmentErrors, setStandardAllotmentErrors] = useState("");
  const [reliefAllotmentErrors, setReliefAllotmentErrors] = useState("");
  const [isReRunAwardWarningDialogOpen, setIsReRunAwardWarningDialogOpen] = useState(false);
  const [awardErrorMessage, setAwardErrorMessage] = useState("");
  // EXPORT RESULTS
  const [exportResultsMenuAnchor, setExportResultsMenuAnchor] = useState(null);

  useEffect(() => {
    setTotalStandardEmployees(getStandardEmployeesCount(props.bidGroup));
    setTotalReliefEmployees(getReliefEmployeesCount(props.bidGroup));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bidGroup]);

  useEffect(() => {
    if (props.schedMonth !== "" && props.schedYear !== "") {
      setStandardAllotmentErrors(
        validateStandardAllotments(
          totalStandardEmployees,
          getTotalStandardAllotments(props.standardDayState),
          props.schedMonth,
          props.schedYear
        )
      );
      setReliefAllotmentErrors(
        validateReliefAllotments(totalReliefEmployees, props.calcReliefAllotments, props.schedMonth, props.schedYear)
      );
    }
  }, [
    totalStandardEmployees,
    totalReliefEmployees,
    props.standardDayState,
    props.calcReliefAllotments,
    props.bidGroup,
    props.schedMonth,
    props.schedYear,
  ]);

  useEffect(() => {
    const filterByRadioButtons = (filterValue) => {
      let filteredRows = [];
      switch (filterValue) {
        case BID_AWARDED_FILTER_OPTIONS.ALL:
          filteredRows = props.freedayAwardRows;
          break;
        case BID_AWARDED_FILTER_OPTIONS.AWARDED:
          filteredRows = props.freedayAwardRows.filter((freeday) => freeday.eid);
          break;
        case BID_AWARDED_FILTER_OPTIONS.UNAWARDED:
          filteredRows = props.freedayAwardRows.filter((freeday) => !freeday.eid);
          break;
        default:
          console.error("Unrecognized filter option");
      }
      setFilteredFreedayAwardRows(filteredRows);
      return filteredRows;
    };

    const filterByAwardTypeCheckBoxes = (filteredRows, awardTypeList) => {
      const awardTypeFilteredRows = filteredRows.filter((line) =>
        line.eid ? awardTypeList.includes(line.awardType) : true
      );
      setFilteredFreedayAwardRows(awardTypeFilteredRows);
      return awardTypeFilteredRows;
    };

    const filterByStandardReliefCheckBoxes = (filteredRows, standardReliefList) => {
      const standardReliefFilteredRows = filteredRows.filter((line) =>
        standardReliefList.includes(line.standardRelief)
      );
      setFilteredFreedayAwardRows(standardReliefFilteredRows);
    };

    let filteredRows = filterByRadioButtons(selectedAwardFilterRadioButton);
    filteredRows = filterByAwardTypeCheckBoxes(filteredRows, selectedAwardFilterCheckBoxList);
    filterByStandardReliefCheckBoxes(filteredRows, selectedStandardReliefFilterCheckBoxList);
  }, [
    props.freedayAwardRows,
    selectedAwardFilterRadioButton,
    selectedAwardFilterCheckBoxList,
    selectedStandardReliefFilterCheckBoxList,
  ]);

  useEffect(() => {
    const unawardedTypeFilteredRows = props.unAwardedList?.filter((line) =>
      line.eid ? selectedUnawardedFilterCheckBoxList.includes(line.awardType) : true
    );
    setFilteredFreedayUnawardedRows(unawardedTypeFilteredRows);
  }, [selectedUnawardedFilterCheckBoxList, props.unAwardedList]);

  const isBidAwardAvailable = () => {
    for (const emp of props.bidGroup) {
      if ("awardType" in emp) {
        return true;
      }
    }
    return false;
  };

  const shouldDisablePublishButton = () => {
    return (
      props.isFormChanged ||
      props.isBidGroupTabChanged ||
      hasUnawardedFreedayEmployees(props.bidGroup) ||
      isAwardedBid(props.bid.status)
    );
  };

  const getPublishButtonToolTipText = () => {
    const employeesWithNoFreeday = hasUnawardedFreedayEmployees(props.bidGroup);

    if (employeesWithNoFreeday && props.isFormChanged) {
      return "Unable to publish. There are unsaved changes and unawarded Employees.";
    }

    if (employeesWithNoFreeday) {
      return "Unable to publish. There are unawarded Employees.";
    }

    if (props.isFormChanged) {
      return "Unable to publish. There are unsaved changes.";
    }

    return "";
  };

  const handleRunAwardClicked = (standardDayState, reliefDayState, bidGroup) => {
    setIsAwardRunning(true);
    runFreedayBidAward(props.bid.referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        const awardResult = processFreedayBidAwardPayload(response, standardDayState, reliefDayState, bidGroup);
        props.setBid({ ...props.bid, awardLog: awardResult.awardLog });
        props.setStandardDayState(awardResult.standardDayState);
        props.setReliefDayState(awardResult.reliefDayState);
        props.setInitialStandardDayState(awardResult.standardDayState);
        props.setInitialReliefDayState(awardResult.reliefDayState);
        props.setInitialBidGroup(awardResult.bidGroup);
        props.setBidGroup(awardResult.bidGroup);
        setIsAwardRunning(false);
      })
      .catch((error) => {
        console.log("Error: ", error);
        setIsAwardRunning(false);
        setAwardErrorMessage("Unexpected Error");
      });
  };

  const handleReRunAwardClicked = () => {
    setIsReRunAwardWarningDialogOpen(true);
  };

  const handleReRunAwardConfirm = () => {
    setIsReRunAwardWarningDialogOpen(false);
    const clearedDataResult = clearFreedayAwardData(props.standardDayState, props.reliefDayState, props.bidGroup);
    props.setStandardDayState(clearedDataResult.standardDayState);
    props.setReliefDayState(clearedDataResult.reliefDayState);
    props.setBidGroup(clearedDataResult.bidGroup);
    handleRunAwardClicked(
      clearedDataResult.standardDayState,
      clearedDataResult.reliefDayState,
      clearedDataResult.bidGroup
    );
  };

  const handleAwardFilterRadioButtonClicked = (event) => {
    setSelectedAwardFilterRadioButton(event.target.value);
  };

  const handleAwardFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedAwardFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedAwardFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleUnawardedFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedUnawardedFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedUnawardedFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleStandardReliefFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedStandardReliefFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedStandardReliefFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleEnlargeBidAllotmentsButtonClicked = (event) => {
    setIsEnlargeBidAllotmentsDialogOpen(true);
  };

  const handleEnlargeBidAllotmentsDialogClosed = (event) => {
    setIsEnlargeBidAllotmentsDialogOpen(false);
  };

  const onPublishSuccess = () => {
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const onPublishError = () => {
    setPublishErrors("Unexpected Error");
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const onPublishConfirm = async () => {
    setShowPublishLoader(true);
    const bidData = { ...props.bid };
    bidData.status = BID_STATUS.AWARDED;
    bidData.scheduledStatus = "";

    // Replace spl. chars with underscore (temp fix for existing bids with spl. chars in the bid name, we can revert it once these bids get awarded)
    bidData.bidName = sanitizeSpecialChars(bidData.bidName);

    const allotmentsPayload = buildFreedayBidAllotmentsPayload(
      props.standardDayState,
      props.reliefDayState,
      props.calcStandardAllotments,
      props.calcReliefAllotments
    );

    try {
      const s3ObjectName = await getS3ObjectName(bidData.pk, bidData.station);
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();

      if (result.presignedUrl) {
        const bidS3Data = buildTimeOffBidS3Data(userSession, bidData, allotmentsPayload, props.bidGroup, s3ObjectName);
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(bidData.referenceNumber, userSession, requestId);
          onPublishSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error in onPublishConfirm: ", err);
      onPublishError();
    }
  };

  const renderPublishButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        component="label"
        onClick={() => setIsPublishConfirmationDialogOpen(true)}
        disabled={shouldDisablePublishButton()}
        sx={{ margin: "5px" }}
      >
        Publish
      </Button>
    );
  };

  const renderPreAwardChecks = () => {
    return (
      <>
        {/* Title */}
        <Grid container sx={{ padding: "10px" }}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
            >
              {props.bid.station} | {props.bid.workgroup}
              {props.schedMonth !== "" &&
                props.schedYear !== "" &&
                ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
              {props.bidName && ` | ${props.bidName}`}
            </Typography>
          </Grid>
        </Grid>

        {/* Bid Allotment/Bid Group Counts Section */}
        <Grid container sx={{ bgcolor: "secondary.swaLightestBlue", padding: "10px" }} alignItems={"center"}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "left" }}>
            <Box sx={{ padding: "5px" }}>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Allotments
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography
                        component="span"
                        sx={{
                          color: standardAllotmentErrors !== "" || reliefAllotmentErrors !== "" ? "primary.swaRed" : "",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {getTotalStandardAllotments(props.standardDayState) +
                          getTotalReliefAllotments(props.reliefDayState)}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Standard:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getTotalStandardAllotments(props.standardDayState)}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Relief:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getTotalReliefAllotments(props.reliefDayState)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Eligible
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography
                        component="span"
                        sx={{
                          color: standardAllotmentErrors !== "" || reliefAllotmentErrors !== "" ? "primary.swaRed" : "",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {getEligibleEmployees(props.bidGroup).length}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Standard:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getStandardEmployeesCount(getEligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Relief:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getReliefEmployeesCount(getEligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {standardAllotmentErrors === "" && reliefAllotmentErrors === "" ? (
          <>
            {/* Checklist */}
            <Grid container sx={{ padding: "1rem", textAlign: "left" }}>
              <Grid item xs={12}>
                <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
                  Check off each item as complete before running the Award:
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAddEmployeeConfirmationChecked}
                        onChange={(event) => setIsAddEmployeeConfirmationChecked(event.target.checked)}
                        name="addEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Add any Employees who entered the Workgroup during the Bid Window"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRemoveEmployeeConfirmationChecked}
                        onChange={(event) => setIsRemoveEmployeeConfirmationChecked(event.target.checked)}
                        name="removeEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Remove any Employees who left the Workgroup after the Bid opened"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEligibilityEmployeeConfirmationChecked}
                        onChange={(event) => setIsEligibilityEmployeeConfirmationChecked(event.target.checked)}
                        name="eligibilityEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Check eligibility of all Employees"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleRunAwardClicked(props.standardDayState, props.reliefDayState, props.bidGroup)}
                  size="large"
                  sx={{ width: "200px" }}
                  startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
                  disabled={
                    !isAddEmployeeConfirmationChecked ||
                    !isRemoveEmployeeConfirmationChecked ||
                    !isEligibilityEmployeeConfirmationChecked ||
                    isAwardRunning ||
                    props.isBidGroupTabChanged ||
                    props.isBidAllotmentsTabChanged
                  }
                >
                  {isAwardRunning ? <CircularProgress size={25} /> : "Run Award"}
                </Button>
              </Grid>
              {awardErrorMessage !== "" && (
                <ErrorMessage
                  errorMessage={bidDataErrorCodeToErrorMessageMap.bidAwardError.message}
                  errorCode={bidDataErrorCodeToErrorMessageMap.bidAwardError.code}
                />
              )}
            </Grid>
          </>
        ) : (
          <Grid container sx={{ padding: "1rem", textAlign: "left" }}>
            <Grid item xs={12}>
              <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
                There are not enough allotments.
              </Typography>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const renderPostAwardResults = () => {
    return (
      <Grid container>
        {/* Station | Workgroup & Export/Print buttons */}
        <Grid
          item
          xs={6}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            paddingTop: "15px",
            paddingBottom: "15px",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "left", fontWeight: "bold" }}>
            {props.bid.station} | {props.bid.workgroup}
            {props.schedMonth !== "" &&
              props.schedYear !== "" &&
              ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
            {props.bidName && ` | ${props.bidName}`}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            paddingRight: "1rem",
            borderBottom: "1px solid",
            color: "secondary.swaGray2",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
            component="label"
            onClick={handleReRunAwardClicked}
            disabled={
              isAwardRunning ||
              props.isBidGroupTabChanged ||
              props.isBidAllotmentsTabChanged ||
              isAwardedBid(props.bid.status)
            }
            sx={{ margin: "5px", width: "200px" }}
          >
            {isAwardRunning ? <CircularProgress size={25} /> : "Re-Run Award"}
          </Button>
          <FreedayBidAwardExport
            bid={props.bid}
            bidAllotments={props.freedayAwardRows}
            exportResultsMenuAnchor={exportResultsMenuAnchor}
            setExportResultsMenuAnchor={setExportResultsMenuAnchor}
            isFormChanged={props.isFormChanged}
            schedMonth={props.schedMonth}
            schedYear={props.schedYear}
            calcStandardAllotments={props.calcStandardAllotments}
            calcReliefAllotments={props.calcReliefAllotments}
          />
          {props.isFormChanged || hasUnawardedFreedayEmployees(props.bidGroup) ? (
            <Tooltip title={getPublishButtonToolTipText()} arrow placement="bottom">
              <span>{renderPublishButton()}</span>
            </Tooltip>
          ) : (
            renderPublishButton()
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="title" sx={{ paddingRight: "20px", fontWeight: "bold", color: "primary.swaBlue" }}>
            Key:
          </Typography>
          <Typography variant="title">
            <strong>AA: </strong> Auto-Awarded
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NEC: </strong> Not Enough Choices
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NB: </strong> No Bid
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NS: </strong> No Schedule
          </Typography>
        </Grid>
        <Grid item xs={6.7}>
          {renderFreedays(false)}
        </Grid>
        <Grid item xs={5.3} sx={{ borderLeft: "5px solid", borderColor: "secondary.swaGray2" }}>
          {renderUnawardedSection()}
        </Grid>
      </Grid>
    );
  };

  const renderFreedays = (isDialog) => {
    return (
      <Grid container sx={{ borderRight: "1px solid", borderColor: "secondary.swaGray2" }}>
        <Grid container sx={{ display: "flex", alignItems: "center", padding: "1rem" }}>
          <Grid item xs={2}>
            <Typography variant="h6" color="primary.swaBlue" fontWeight="bold" textAlign="left">
              Freedays
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <FormControl>
              <RadioGroup
                value={selectedAwardFilterRadioButton}
                onChange={handleAwardFilterRadioButtonClicked}
                row
                name="row-radio-buttons-group"
                sx={{ alignItems: "center" }}
              >
                <Typography sx={{ marginRight: "10px", fontWeight: "bold", color: "secondary.swaBlack" }}>
                  Show:{" "}
                </Typography>
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.ALL}
                  control={<Radio />}
                  label={`All (${props.freedayAwardRows.length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.UNAWARDED}
                  control={<Radio />}
                  label={`Available (${props.freedayAwardRows.filter((line) => !line.eid).length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.AWARDED}
                  control={<Radio />}
                  label={`Awarded (${props.freedayAwardRows.filter((line) => !!line.eid).length})`}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={1} sx={{ textAlign: "right", paddingRight: "1rem" }}>
            {isDialog ? (
              <StarsCloseIconButton onClick={handleEnlargeBidAllotmentsDialogClosed} />
            ) : (
              <IconButton sx={{ padding: 0 }} onClick={handleEnlargeBidAllotmentsButtonClicked}>
                <LaunchIcon color="primary" />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", textAlign: "left" }}>
          <FormControl>
            <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                Award Filter:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.AUTO_AWARDED)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={FREEDAY_BID_AWARD_TYPE.AUTO_AWARDED}
                  />
                }
                label={FREEDAY_BID_AWARD_TYPE.AUTO_AWARDED}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                  />
                }
                label={FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.NO_BID)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={FREEDAY_BID_AWARD_TYPE.NO_BID}
                  />
                }
                label={FREEDAY_BID_AWARD_TYPE.NO_BID}
              />
              {selectedAwardFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one award type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
          <FormControl sx={{ borderLeft: "1px solid", borderColor: "secondary.swaLightBlue", flexGrow: 1 }}>
            <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                Standard/Relief:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStandardReliefFilterCheckBoxList.includes(SELECT_FREEDAY_BID_TYPE_LIST[0])}
                    onChange={handleStandardReliefFilterCheckBoxClicked}
                    value={SELECT_FREEDAY_BID_TYPE_LIST[0]}
                  />
                }
                label={SELECT_FREEDAY_BID_TYPE_LIST[0]}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStandardReliefFilterCheckBoxList.includes(SELECT_FREEDAY_BID_TYPE_LIST[1])}
                    onChange={handleStandardReliefFilterCheckBoxClicked}
                    value={SELECT_FREEDAY_BID_TYPE_LIST[1]}
                  />
                }
                label={SELECT_FREEDAY_BID_TYPE_LIST[1]}
              />
              {selectedStandardReliefFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one Standard/Relief type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              height: isDialog ? "80vh" : `calc(86vh - 240px)`,
              width: "inherit",
              "& .gray-color-cell": {
                backgroundColor: "secondary.swaGray3",
              },
            }}
            data-testid="bid-lines-grid"
          >
            <DataGridPro
              sx={{
                border: 0,
                "& div": {
                  "& .MuiDataGrid-footerContainer": {
                    display: "none",
                  },
                  "& .MuiDataGrid-cell": { whiteSpace: "normal !important", textAlign: "left" },
                },
                borderRadius: "0px",
                "& .MuiDataGrid-columnHeaderTitle": {
                  textOverflow: "clip",
                  whiteSpace: "break-spaces",
                  lineHeight: 1.2,
                  textAlign: "left",
                },
              }}
              columns={getFreedayColumns({
                station: props.bid.station,
                handleAwardedNameClick: props.handleAwardedNameClick,
              })}
              getRowId={(row) => row.id}
              rows={filteredFreedayAwardRows}
              rowHeight={60}
              components={{
                LoadingOverlay: customLoadingTableOverlay,
                NoRowsOverlay: CustomNoRowsTableOverlay,
                ErrorOverlay: CustomErrorTableOverlay,
              }}
              componentsProps={{
                noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
              }}
              loading={props.isBidAllotmentsLoading}
              disableColumnMenu={true}
              showColumnVerticalBorder={true}
              showCellVerticalBorder={true}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderEnlargeBidAllotmentsDialog = () => {
    return (
      <Dialog onClose={handleEnlargeBidAllotmentsDialogClosed} open={isEnlargeBidAllotmentsDialogOpen} fullScreen>
        {renderFreedays(true)}
      </Dialog>
    );
  };

  const renderUnawardedSection = () => {
    return (
      <Grid container>
        <Box sx={{ width: "100%" }}>
          <Grid item xs={12} sx={{ padding: "1rem", textAlign: "left", marginTop: "8px" }}>
            <Typography variant="h6" color="primary.swaBlue" fontWeight={"bold"}>
              {`Unawarded (${props.unAwardedList?.length})`}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              paddingLeft: "1rem",
              paddingTop: "10px",
              paddingBottom: "10px",
              display: "flex",
              backgroundColor: "secondary.swaLightestBlue",
            }}
          >
            <Typography sx={{ paddingRight: "30px" }}>
              Total: <strong>{props.unAwardedList?.length}</strong>
            </Typography>
            <Typography sx={{ paddingRight: "30px" }}>
              Standard:{" "}
              <strong>{props.unAwardedList?.filter((emp) => emp.standardRelief === "Standard").length}</strong>
            </Typography>
            <Typography>
              Relief: <strong>{props.unAwardedList?.filter((emp) => emp.standardRelief === "Relief").length}</strong>
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              paddingLeft: "1rem",
              paddingTop: "10px",
              paddingBottom: "10px",
              display: "flex",
              backgroundColor: "secondary.swaLightestBlue",
            }}
          >
            <FormControl>
              <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
                <Typography
                  sx={{
                    marginRight: "10px",
                    fontWeight: "bold",
                    color: "secondary.swaBlack",
                    fontSize: "14px",
                  }}
                >
                  Unawarded Filter:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                    />
                  }
                  label={FREEDAY_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.NO_BID)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={FREEDAY_BID_AWARD_TYPE.NO_BID}
                    />
                  }
                  label={FREEDAY_BID_AWARD_TYPE.NO_BID}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(FREEDAY_BID_AWARD_TYPE.NO_SCHEDULE)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={FREEDAY_BID_AWARD_TYPE.NO_SCHEDULE}
                    />
                  }
                  label={FREEDAY_BID_AWARD_TYPE.NO_SCHEDULE}
                />
                {selectedUnawardedFilterCheckBoxList.length === 0 && (
                  <FormHelperText error>At least one award type must be selected</FormHelperText>
                )}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                height: `calc(85vh - 230px)`,
                "& .gray-color-cell": {
                  backgroundColor: "secondary.swaGray3",
                },
              }}
            >
              <DataGridPro
                sx={{
                  border: 0,
                  "& div": {
                    "& .MuiDataGrid-footerContainer": {
                      display: "none",
                    },
                  },
                  borderRadius: "0px",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textOverflow: "clip",
                    whiteSpace: "break-spaces",
                    lineHeight: 1.2,
                    textAlign: "left",
                  },
                }}
                columns={getUnAwardedTableColumns({
                  station: props.bid.station,
                  bidReferenceNumber: props.bid.referenceNumber,
                  bidStatus: props.bid.status,
                })}
                rows={filteredFreedayUnawardedRows}
                rowHeight={60}
                components={{
                  LoadingOverlay: customLoadingTableOverlay,
                  NoRowsOverlay: CustomNoRowsTableOverlay,
                  ErrorOverlay: CustomErrorTableOverlay,
                }}
                componentsProps={{
                  noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
                }}
                loading={props.isBidAllotmentsLoading}
                disableColumnMenu={true}
                showColumnVerticalBorder={true}
                showCellVerticalBorder={true}
              />
            </Box>
          </Grid>
        </Box>
      </Grid>
    );
  };

  const renderBidGroupChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Group changes must be saved before you can run the award. This will delete any existing award results.
        </Alert>
      </Grid>
    );
  };

  const renderBidAllotmentsChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Allotment changes must be saved before you can run the award. This will delete the existing award results.
        </Alert>
      </Grid>
    );
  };

  return (
    <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
      {!props.isBidGroupLoading && !props.isBidAllotmentsLoading ? (
        <>
          {props.isBidGroupTabChanged && renderBidGroupChangesDetectedAlert()}
          {props.isBidAllotmentsTabChanged && renderBidAllotmentsChangesDetectedAlert()}
          {isBidAwardAvailable() ? renderPostAwardResults() : renderPreAwardChecks()}
          {isEnlargeBidAllotmentsDialogOpen && renderEnlargeBidAllotmentsDialog()}
          {isReRunAwardWarningDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isReRunAwardWarningDialogOpen}
              title="Re-run Award"
              message="You are running the award again. This will delete the existing award results."
              handleNoButtonClicked={() => setIsReRunAwardWarningDialogOpen(false)}
              handleYesButtonClicked={handleReRunAwardConfirm}
              isYesLoading={isAwardRunning}
              noTitle="Cancel"
              yesTitle="Re-Run Award"
            />
          )}
          {isPublishConfirmationDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isPublishConfirmationDialogOpen}
              title="Publish TWU Freeday Bid"
              message={FREEDAY_BID_PUBLISH_DIALOG_MESSAGE(
                props.bid.station,
                props.bid.workgroup,
                props.schedMonth,
                props.schedYear
              )}
              handleNoButtonClicked={() => setIsPublishConfirmationDialogOpen(false)}
              handleYesButtonClicked={onPublishConfirm}
              isYesLoading={showPublishLoader}
              noTitle="Cancel"
              yesTitle="PUBLISH"
            />
          )}
          {isSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              message={"The bid has been successfully published!"}
              hasError={publishErrors !== ""}
              error={publishErrors}
              redirectTo={publishErrors === "" && props.bidAdminPath}
              handleOKClick={() => {
                setPublishErrors("");
                setIsSuccessDialogOpen(false);
                publishErrors !== "" && props.loadBidData();
              }}
            />
          )}
        </>
      ) : (
        <div style={{ width: "100%" }}>
          <LinearProgress />
        </div>
      )}
    </Grid>
  );
};

FreedayBidAward.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  isBidAllotmentsLoading: PropTypes.bool.isRequired,
  isBidGroupTabChanged: PropTypes.bool.isRequired,
  isBidAllotmentsTabChanged: PropTypes.bool.isRequired,
  calcStandardAllotments: PropTypes.object.isRequired,
  calcReliefAllotments: PropTypes.object.isRequired,
  freedayAwardRows: PropTypes.array.isRequired,
  standardDayState: PropTypes.array.isRequired,
  reliefDayState: PropTypes.array.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  bidName: PropTypes.string,
  unAwardedList: PropTypes.array,
  bidAdminPath: PropTypes.string.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  loadBidData: PropTypes.func.isRequired,
  setBid: PropTypes.func.isRequired,
  setStandardDayState: PropTypes.func.isRequired,
  setReliefDayState: PropTypes.func.isRequired,
  handleAwardedNameClick: PropTypes.func.isRequired,
  currentRequestId: PropTypes.object.isRequired,
  setInitialStandardDayState: PropTypes.func.isRequired,
  setInitialReliefDayState: PropTypes.func.isRequired,
  setInitialBidGroup: PropTypes.func.isRequired,
};

export default FreedayBidAward;
